import { computed, ref, shallowRef } from 'vue'
import { ESTIMATED_SECONDS, TCP_TARGETS, TIMEOUTS } from '@/config/targets'
import { checkTcpTarget } from '@/lib/tcp-probe'
import { fetchServerSeenIp } from '@/lib/public-ip'
import { runUdpCheck, type UdpCheckResult } from '@/lib/udp-check'
import { deriveOverallStatus, type TcpStatus, type UdpStatus } from '@/lib/status'
import { combineStatus, type CallOutcome } from '@/lib/run-status'
import {
	CallTestApiError,
	createSession,
	finishSession,
	readResult,
	releaseSessionOnExit
} from '@/lib/calltest/api'
import type { ApiFailure } from '@/lib/calltest/api-failure'
import { runCall, type CallFailure, type CallPhase } from '@/lib/calltest/sip'
import { startLevelMeter, type LevelMeter } from '@/lib/calltest/levels'
import { summarise, type BrowserMeasurement, type StatsSample } from '@/lib/calltest/stats'
import { gradeBrowserCall, type BrowserVerdict } from '@/lib/calltest/grading'
import type { CallTestResult } from '@/lib/calltest/types'

export type RunPhase = 'idle' | 'firewall' | 'provisioning' | CallPhase | 'analysing' | 'result'

export interface TcpCheck {
	label: string
	host: string
	status: TcpStatus
}

/**
 * One run of the whole test: the firewall probes first, then a real call through the platform.
 *
 * Sequential rather than parallel, for two reasons. The SIP credentials live only five minutes,
 * so they are fetched immediately before dialling; and when the firewall probes come back blocked,
 * the call's failure is then already explained rather than being a second mystery.
 */
export function useConnectionCheck() {
	const phase = ref<RunPhase>('idle')
	const tcpChecks = ref<TcpCheck[]>(freshTcpChecks())
	const udpStatus = ref<UdpStatus>('pending')
	const udpResult = shallowRef<UdpCheckResult | null>(null)
	const serverSeenIp = ref<string | null>(null)

	const apiFailure = shallowRef<ApiFailure | null>(null)
	const callFailure = ref<CallFailure | null>(null)
	const platformResult = shallowRef<CallTestResult | null>(null)
	const platformStillPending = ref(false)
	const browserMeasurement = shallowRef<BrowserMeasurement | null>(null)
	const browserVerdict = shallowRef<BrowserVerdict | null>(null)
	const callConnected = ref(false)
	/** Media time the browser measured, answer to hangup. */
	const browserMediaMs = ref<number | null>(null)

	/** Live meter readings, mirrored into refs so the template can animate them. */
	const remoteLevel = ref(0)
	const toneLevel = ref(0)
	const remoteAudibleMs = ref(0)
	const remoteElapsedMs = ref(0)
	const tonePeak = ref(0)

	const secondsLeft = ref(ESTIMATED_SECONDS)
	const audioEl = ref<HTMLAudioElement | null>(null)

	let abort: AbortController | null = null
	let liveSessionId: string | null = null
	let exitHandler: (() => void) | null = null

	const running = computed(() => phase.value !== 'idle' && phase.value !== 'result')
	const started = computed(() => phase.value !== 'idle')

	const firewallStatus = computed(() => deriveOverallStatus(
		tcpChecks.value.map((check) => check.status),
		udpStatus.value
	))

	const callOutcome = computed<CallOutcome>(() => {
		if (apiFailure.value || callFailure.value) return { kind: 'failed-to-start' }
		if (!browserVerdict.value) return { kind: 'pending' }
		if (!callConnected.value) return { kind: 'not-connected' }
		return { kind: 'graded', verdict: platformOrBrowserVerdict.value }
	})

	/**
	 * The platform's verdict wins when it exists: it measured the leg from the other side and can
	 * see things the browser cannot. The browser's own grading stands in when it does not.
	 */
	const platformOrBrowserVerdict = computed(() => {
		const platform = platformResult.value?.quality
		if (platform && platform.verdict !== 'NOT_MEASURED') return platform.verdict
		if (platform && !browserVerdict.value) return platform.verdict
		return browserVerdict.value?.verdict ?? 'NOT_MEASURED'
	})

	const overall = computed(() => phase.value === 'idle'
		? 'run' as const
		: combineStatus(firewallStatus.value, callOutcome.value))

	const publicIp = computed(() => udpResult.value?.publicIp ?? serverSeenIp.value)

	/** Four TCP probes, the UDP probe, provisioning, the call and the platform's answer. */
	const totalSteps = TCP_TARGETS.length + 4
	const completedSteps = computed(() => {
		let done = tcpChecks.value.filter((check) => check.status !== 'pending').length
		if (udpStatus.value !== 'pending') done += 1
		if (liveSessionId || apiFailure.value) done += 1
		if (browserVerdict.value || callFailure.value) done += 1
		if (platformResult.value || platformStillPending.value || apiFailure.value) done += 1
		return Math.min(done, totalSteps)
	})
	const progress = computed(() => Math.round((completedSteps.value / totalSteps) * 100))

	async function run() {
		if (running.value) return

		resetState()
		abort = new AbortController()
		const stopCountdown = startCountdown(secondsLeft)

		try {
			await runFirewallChecks()
			await runCallTest(abort.signal)
		} finally {
			stopCountdown()
			phase.value = 'result'
		}
	}

	/** The visitor pressing "stop testen". Ends the call; the result screen still gets shown. */
	function stop() {
		abort?.abort()
	}

	async function runFirewallChecks() {
		phase.value = 'firewall'

		await Promise.all([
			...tcpChecks.value.map(async (check, index) => {
				const reachable = await checkTcpTarget(check.host)
				tcpChecks.value[index].status = reachable ? 'ok' : 'blocked'
			}),
			runUdpCheck().then((result) => {
				udpResult.value = result
				udpStatus.value = result.status
			}),
			fetchServerSeenIp().then((ip) => { serverSeenIp.value = ip })
		])
	}

	async function runCallTest(signal: AbortSignal) {
		phase.value = 'provisioning'

		let session
		try {
			session = await createSession()
		} catch (error) {
			apiFailure.value = error instanceof CallTestApiError ? error.failure : null
			return
		}

		liveSessionId = session.sessionId
		// A closing tab must still hand the SIP account back, or it holds one of the 50 slots
		// until the server's reaper collects it and the next visitor may get a 409.
		exitHandler = () => releaseSessionOnExit(session.sessionId)
		window.addEventListener('pagehide', exitHandler)

		const meters: LevelMeter[] = []
		let mediaMs = 0

		try {
			const element = audioEl.value
			if (!element) {
				callFailure.value = 'media-failed'
				return
			}

			const outcome = await runCall({
				session,
				audioEl: element,
				signal,
				hooks: {
					onPhase: (next) => { phase.value = next },
					onSample: (sample) => applySample(sample),
					onRemoteStream: (stream) => meters.push(trackMeter(stream, remoteLevel, {
						audibleMs: remoteAudibleMs,
						elapsedMs: remoteElapsedMs
					})),
					onLocalStream: (stream) => meters.push(trackMeter(stream, toneLevel, { peak: tonePeak }))
				}
			})

			mediaMs = outcome.mediaMs
			browserMediaMs.value = outcome.mediaMs
			callConnected.value = outcome.connected
			callFailure.value = outcome.failure

			const measurement = summarise(outcome.samples)
			browserMeasurement.value = measurement
			browserVerdict.value = gradeBrowserCall(measurement, mediaMs)
		} finally {
			meters.forEach((meter) => meter.stop())
			stopRemoteAudio()
		}

		await collectPlatformResult(session.sessionId)
	}

	/**
	 * Hands the account back and takes the platform's own measurement. A PENDING is neither a
	 * failure nor an outcome — the server resolves every one within 60 s of the call ending, so
	 * this polls up to that and then falls back to the browser's numbers.
	 */
	async function collectPlatformResult(sessionId: string) {
		phase.value = 'analysing'

		try {
			let result = await finishSession(sessionId)
			const deadline = Date.now() + TIMEOUTS.pendingBudget

			while (result.status === 'PENDING' && Date.now() < deadline) {
				await delay(result.retryAfterMs ?? 1000)
				result = await readResult(sessionId)
			}

			platformResult.value = result
			platformStillPending.value = result.status === 'PENDING'
		} catch (error) {
			// The call itself may have gone fine; only the platform's view of it is missing.
			if (error instanceof CallTestApiError) apiFailure.value = error.failure
		} finally {
			releaseExitHandler()
			liveSessionId = null
		}
	}

	function applySample(sample: StatsSample) {
		// Kept so the live view has something to show before the call ends.
		browserMeasurement.value = summarise([...(browserMeasurement.value?.samples ?? []), sample])
	}

	function trackMeter(
		stream: MediaStream,
		level: { value: number },
		extra: { peak?: { value: number }, audibleMs?: { value: number }, elapsedMs?: { value: number } }
	): LevelMeter {
		const meter = startLevelMeter(stream)
		const timer = setInterval(() => {
			level.value = meter.level
			if (extra.peak) extra.peak.value = meter.peak
			if (extra.audibleMs) extra.audibleMs.value = meter.audibleMs
			if (extra.elapsedMs) extra.elapsedMs.value = meter.elapsedMs
		}, 100)

		return {
			get level() { return meter.level },
			get peak() { return meter.peak },
			get audibleMs() { return meter.audibleMs },
			get elapsedMs() { return meter.elapsedMs },
			stop() {
				clearInterval(timer)
				meter.stop()
			}
		}
	}

	function stopRemoteAudio() {
		const element = audioEl.value
		if (!element) return
		element.pause()
		element.srcObject = null
	}

	function releaseExitHandler() {
		if (!exitHandler) return
		window.removeEventListener('pagehide', exitHandler)
		exitHandler = null
	}

	function resetState() {
		// A previous run's session must not outlive it.
		if (liveSessionId) releaseSessionOnExit(liveSessionId)
		releaseExitHandler()
		liveSessionId = null

		tcpChecks.value = freshTcpChecks()
		udpStatus.value = 'pending'
		udpResult.value = null
		serverSeenIp.value = null
		apiFailure.value = null
		callFailure.value = null
		platformResult.value = null
		platformStillPending.value = false
		browserMeasurement.value = null
		browserVerdict.value = null
		callConnected.value = false
		browserMediaMs.value = null
		remoteLevel.value = 0
		toneLevel.value = 0
		remoteAudibleMs.value = 0
		remoteElapsedMs.value = 0
		tonePeak.value = 0
	}

	return {
		phase,
		running,
		started,
		tcpChecks,
		udpStatus,
		udpResult,
		publicIp,
		firewallStatus,
		overall,
		apiFailure,
		callFailure,
		callConnected,
		platformResult,
		platformStillPending,
		browserMeasurement,
		browserMediaMs,
		browserVerdict,
		effectiveVerdict: platformOrBrowserVerdict,
		remoteLevel,
		toneLevel,
		remoteAudibleMs,
		remoteElapsedMs,
		tonePeak,
		completedSteps,
		totalSteps,
		progress,
		secondsLeft,
		audioEl,
		run,
		stop
	}
}

function freshTcpChecks(): TcpCheck[] {
	return TCP_TARGETS.map((target) => ({ label: target.label, host: target.host, status: 'pending' }))
}

function startCountdown(secondsLeft: { value: number }) {
	secondsLeft.value = ESTIMATED_SECONDS
	const timer = setInterval(() => {
		secondsLeft.value = Math.max(0, secondsLeft.value - 1)
	}, 1000)

	return () => clearInterval(timer)
}

function delay(ms: number): Promise<void> {
	return new Promise((resolve) => setTimeout(resolve, ms))
}
